import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  mergePersonaPatches,
  PERSONA_DEFINITIONS,
  PERSONA_MANAGED_KEYS,
} from '@/components/a11y/a11y-personas';
import type {
  A11yManualPreferences,
  A11yPreferencesPatch,
  PersonaId,
} from '@/types/a11y-preferences';

export type {
  A11yManualPreferences,
  A11yPreferencesPatch,
  DisplayLayoutPreference,
  PersonaId,
  PicklistStyle,
  ReducedMotionPreference,
  RequiredFieldStyle,
} from '@/types/a11y-preferences';

export const A11Y_FONT_SIZE_MIN = 75;
export const A11Y_FONT_SIZE_MAX = 125;
export const A11Y_FONT_SIZE_DEFAULT = 100;

export const A11Y_ZOOM_MIN = 75;
export const A11Y_ZOOM_MAX = 150;
export const A11Y_ZOOM_DEFAULT = 100;

export interface A11yPreferencesState {
  manual: A11yManualPreferences;
  menuOpen: boolean;
  setFontSizePercent: (value: number) => void;
  resetFontSizePercent: () => void;
  setZoomPercent: (value: number) => void;
  resetZoomPercent: () => void;
  patchManual: (patch: A11yPreferencesPatch) => void;
  setPersona: (id: PersonaId, enabled: boolean) => void;
  setMenuOpen: (open: boolean) => void;
  resetAll: () => void;
}

function clampFontSize(value: number): number {
  return Math.min(A11Y_FONT_SIZE_MAX, Math.max(A11Y_FONT_SIZE_MIN, Math.round(value)));
}

function clampZoom(value: number): number {
  return Math.min(A11Y_ZOOM_MAX, Math.max(A11Y_ZOOM_MIN, Math.round(value)));
}

export const A11Y_MANUAL_DEFAULTS: Omit<A11yManualPreferences, 'activePersonas'> = {
  fontSizePercent: A11Y_FONT_SIZE_DEFAULT,
  zoomPercent: A11Y_ZOOM_DEFAULT,
  adaptFontToZoom: false,
  highContrast: false,
  monochrome: false,
  readingMask: false,
  focusRingEmphasis: true,
  underlineLinks: false,
  toggleOnOffLabels: false,
  requiredFieldStyle: 'asterisk',
  requiredFieldsRed: true,
  strikethroughDisabled: false,
  dyslexiaFont: false,
  picklistStyle: 'bold',
  reducedMotion: 'system',
  emphasizeFocusArea: false,
  customCursor: false,
  customScrollbar: false,
  highlightCritical: false,
  displayLayout: 'adaptive',
};

function defaultManual(): A11yManualPreferences {
  return { ...A11Y_MANUAL_DEFAULTS, activePersonas: {} };
}

function activePersonaIds(personas: Partial<Record<PersonaId, boolean>>): PersonaId[] {
  return PERSONA_DEFINITIONS.map((p) => p.id).filter((id) => personas[id]);
}

export function getEffectiveA11yPreferences(manual: A11yManualPreferences): A11yManualPreferences {
  const ids = activePersonaIds(manual.activePersonas);
  const personaPatch = mergePersonaPatches(ids);
  const effective = { ...manual };
  for (const key of PERSONA_MANAGED_KEYS) {
    if (key in personaPatch) {
      const value = personaPatch[key];
      if (value !== undefined) {
        Object.assign(effective, { [key]: value });
      }
    }
  }
  return effective;
}

function personasAfterManualEdit(
  activePersonas: Partial<Record<PersonaId, boolean>>,
  patch: A11yPreferencesPatch,
): Partial<Record<PersonaId, boolean>> {
  const keys = Object.keys(patch) as (keyof A11yPreferencesPatch)[];
  const next = { ...activePersonas };
  for (const def of PERSONA_DEFINITIONS) {
    if (!next[def.id]) continue;
    if (keys.some((key) => key in def.patch)) {
      next[def.id] = false;
    }
  }
  return next;
}

export const useA11yPreferencesStore = create<A11yPreferencesState>()(
  persist(
    (set, get) => ({
      manual: defaultManual(),
      menuOpen: false,
      setFontSizePercent: (value) =>
        set((s) => ({
          manual: {
            ...s.manual,
            fontSizePercent: clampFontSize(value),
            activePersonas: personasAfterManualEdit(s.manual.activePersonas, {
              fontSizePercent: value,
            }),
          },
        })),
      resetFontSizePercent: () =>
        set((s) => ({
          manual: {
            ...s.manual,
            fontSizePercent: A11Y_FONT_SIZE_DEFAULT,
            activePersonas: personasAfterManualEdit(s.manual.activePersonas, {
              fontSizePercent: A11Y_FONT_SIZE_DEFAULT,
            }),
          },
        })),
      setZoomPercent: (value) =>
        set((s) => ({
          manual: {
            ...s.manual,
            zoomPercent: clampZoom(value),
            activePersonas: personasAfterManualEdit(s.manual.activePersonas, { zoomPercent: value }),
          },
        })),
      resetZoomPercent: () =>
        set((s) => ({
          manual: {
            ...s.manual,
            zoomPercent: A11Y_ZOOM_DEFAULT,
            activePersonas: personasAfterManualEdit(s.manual.activePersonas, {
              zoomPercent: A11Y_ZOOM_DEFAULT,
            }),
          },
        })),
      patchManual: (patch) =>
        set((s) => ({
          manual: {
            ...s.manual,
            ...patch,
            activePersonas: personasAfterManualEdit(s.manual.activePersonas, patch),
          },
        })),
      setPersona: (id, enabled) =>
        set((s) => ({
          manual: {
            ...s.manual,
            activePersonas: { ...s.manual.activePersonas, [id]: enabled },
          },
        })),
      setMenuOpen: (open) => set({ menuOpen: open }),
      resetAll: () => set({ manual: defaultManual(), menuOpen: get().menuOpen }),
    }),
    {
      name: 'trove-a11y-preferences',
      version: 3,
      migrate: (persisted) => {
        const prev = persisted as Record<string, unknown>;
        if (prev.manual && typeof prev.manual === 'object') {
          return {
            manual: { ...defaultManual(), ...(prev.manual as object) },
            menuOpen: Boolean(prev.menuOpen),
          };
        }
        const { menuOpen, activePersonas, ...rest } = prev;
        return {
          manual: {
            ...defaultManual(),
            ...(rest as A11yPreferencesPatch),
            activePersonas: (activePersonas as Partial<Record<PersonaId, boolean>>) ?? {},
          },
          menuOpen: Boolean(menuOpen),
        };
      },
    },
  ),
);

/** Selector-friendly effective prefs (manual + active persona overlays). */
export function useEffectiveA11yPreferences(): A11yManualPreferences {
  const manual = useA11yPreferencesStore((s) => s.manual);
  return getEffectiveA11yPreferences(manual);
}
